import {AnswerStatus, QuestionDifficulty} from '@domain/pom'
import {Box} from '@mui/material'
import React, {useMemo, useState, useCallback} from 'react'

import {LevelCompletion, QuestionDetail, Questionnaire} from '../components'
import {
  useQuestions,
  useSubmitAnswer,
  usePlantUpmId,
  useQuestionDifficultyParam,
  useStringSearchParameter
} from '../hooks'
import {QuestionAnswer} from '../types'
import {mapToQuestionItems, doesAnswerCompleteCategory} from '../utils'

export const QuestionnaireContainer: React.FC = () => {
  const plantId = usePlantUpmId()
  const questionList = useQuestions()
  const [difficulty, setDifficulty] = useQuestionDifficultyParam()

  const [selectedQuestionId, setSelectedQuestionId] = useStringSearchParameter('selectedQuestionId')
  const selectedQuestion = useMemo(
    () => mapToQuestionItems(questionList.data ?? []).find(({id}) => id === selectedQuestionId),
    [questionList.data, selectedQuestionId]
  )
  const [showCompletionDialog, setShowCompletionDialog] = useState(false)
  const onSuccess = useCallback(
    (result: QuestionAnswer) => {
      if (doesAnswerCompleteCategory(result, questionList.data ?? [])) {
        setShowCompletionDialog(true)
      }
    },
    [questionList.data]
  )
  const {mutate: submitAnswer} = useSubmitAnswer(onSuccess)
  const onAnswerSelected = (id: string, answer: AnswerStatus) => {
    submitAnswer({
      plantId,
      questionId: id,
      answer
    })
  }

  const onDifficultyChanged = (questionDifficulty: QuestionDifficulty) => {
    setDifficulty(questionDifficulty)
  }

  return (
    <Box mx={1.5}>
      {selectedQuestion ? (
        <QuestionDetail
          selectedQuestion={selectedQuestion}
          onClose={() => setSelectedQuestionId(undefined)}
          onSelected={onAnswerSelected}
        />
      ) : (
        <Questionnaire
          questionList={questionList}
          onAnswerSelected={onAnswerSelected}
          boxSx={{flex: 1}}
          difficulty={difficulty}
          onDifficultyChanged={onDifficultyChanged}
        />
      )}
      {showCompletionDialog && (
        <LevelCompletion
          open={showCompletionDialog}
          sectionCompleted={difficulty}
          onOpenChange={(open) => setShowCompletionDialog(open)}
        />
      )}
    </Box>
  )
}
